export function shouldRenderAssistantErrorCard(error) {
  if (!error) {
    return false;
  }
  const record = Object.assign({}, error);
  const severity = String(record.severity ?? '').trim().toLowerCase();
  const type = String(record.type ?? '').trim().toLowerCase();
  if ([severity, type].includes('degraded')) {
    return false;
  }
  if (record.fatal === false) {
    return false;
  }
  return true;
}

const ASSISTANT_ERROR_TITLE = '暂时无法生成回复';
const ASSISTANT_REGENERATE_MESSAGE = '生成遇到问题，请重新生成。';
const NETWORK_ERROR_MESSAGE = '网络连接异常，请稍后再试。';
const PROVIDER_PLACEHOLDER_PATTERN = /\bLLM\s+placeholder\b/i;
// Backend codes identify failures without exposing provider payloads or changing recovery authority.
const ERROR_CODE_DISPLAYS = Object.freeze({
  INTERNAL_ERROR: {
    title: '服务处理失败', userMessage: '服务处理失败，请联系管理员。错误代码：INTERNAL_ERROR。', actionLabel: '',
  },
  RUNTIME_DOMAIN_CONFIG_UNAVAILABLE: {
    title: '当前工作空间不可用', userMessage: '当前工作空间配置不可用，请联系管理员。错误代码：RUNTIME_DOMAIN_CONFIG_UNAVAILABLE。', actionLabel: '',
  },
  KERNEL_TRANSPORT_UNAVAILABLE: {
    title: '服务连接异常', userMessage: '服务连接异常，请联系管理员。错误代码：KERNEL_TRANSPORT_UNAVAILABLE。', actionLabel: '',
  },
  KERNEL_REQUEST_TIMEOUT: {
    title: '服务响应超时', userMessage: '服务响应超时，请联系管理员。错误代码：KERNEL_REQUEST_TIMEOUT。', actionLabel: '',
  },
  KERNEL_REQUEST_FAILED: {
    title: '服务处理失败', userMessage: '服务处理失败，请联系管理员。错误代码：KERNEL_REQUEST_FAILED。', actionLabel: '',
  },
  INVALID_KERNEL_RESPONSE: {
    title: '服务响应异常', userMessage: '服务响应异常，请联系管理员。错误代码：INVALID_KERNEL_RESPONSE。', actionLabel: '',
  },
});
const RECOVERY_DISPLAYS = Object.freeze({
  auto_executor_resolution_invalid: {
    title: '任务服务配置无效', userMessage: '当前任务服务配置未通过校验，本轮已停止。请联系管理员检查配置，已保存的内容仍保留。', actionLabel: '',
  },
  auto_executor_resolution_failed: {
    title: '任务服务初始化失败', userMessage: '当前任务服务未能完成初始化，本轮已停止。请联系管理员检查服务，已保存的内容仍保留。', actionLabel: '',
  },
  executor_adapter_not_registered: {
    title: '任务服务尚未接入', userMessage: '当前任务所需服务尚未接入，本轮已停止。请联系管理员完成配置，已保存的内容仍保留。', actionLabel: '',
  },
  execution_timeout: {
    title: '任务执行超时', userMessage: '任务未在规定时间内完成，已结束本轮处理。已保存的内容仍保留，请稍后继续。', actionLabel: '',
  },
  execution_quota_exhausted: {
    title: '任务所需模型配额不可用', userMessage: '模型服务无法提供任务所需配额。请联系管理员检查额度或限额，已保存的内容仍保留。', actionLabel: '',
  },
  execution_rate_limited: {
    title: '任务服务请求过多', userMessage: '任务服务正在限流，已结束本轮处理。已保存的内容仍保留，请稍后继续。', actionLabel: '',
  },
  execution_auth_failed: {
    title: '任务服务鉴权失败', userMessage: '任务服务凭证或权限未通过验证。请联系管理员检查配置，已保存的内容仍保留。', actionLabel: '',
  },
  execution_context_limit_exceeded: {
    title: '任务上下文超出限制', userMessage: '任务输入超过模型上下文上限。请缩小处理范围或联系管理员调整配置，已保存的内容仍保留。', actionLabel: '',
  },
  execution_output_limit_exceeded: {
    title: '任务结果达到长度限制', userMessage: '任务结果达到模型输出上限，本次未完成。请缩小处理范围，已保存的内容仍保留。', actionLabel: '',
  },
  execution_content_filtered: {
    title: '任务内容未通过服务检查', userMessage: '本次任务内容未通过模型服务检查。请调整请求后继续，已保存的内容仍保留。', actionLabel: '',
  },
  execution_input_limit_exceeded: {
    title: '任务输入超出执行限制', userMessage: '任务输入超过当前执行预算，已结束本轮处理。请缩小处理范围，已保存的内容仍保留。', actionLabel: '',
  },
  execution_invalid_response: {
    title: '任务结果未通过校验', userMessage: '任务返回结果未通过格式或内容合同校验，已结束本轮处理。已保存的内容仍保留。', actionLabel: '',
  },
  execution_provider_unavailable: {
    title: '任务服务暂不可用', userMessage: '任务服务暂时无法完成请求，已结束本轮处理。已保存的内容仍保留，请稍后继续。', actionLabel: '',
  },
  execution_network_error: {
    title: '任务服务连接失败', userMessage: '无法连接任务服务，已结束本轮处理。已保存的内容仍保留，请稍后继续。', actionLabel: '',
  },
  execution_failed: {
    title: '任务执行未完成', userMessage: '本次任务未能完成，已停止继续尝试并保留已保存的内容。', actionLabel: '',
  },
  model_context_limit_exceeded: {
    title: '上下文超出限制', userMessage: '当前上下文超过模型处理上限。请减少本次输入，或联系管理员调整配置。', actionLabel: '',
  },
  model_output_limit_exceeded: {
    title: '回复达到长度限制', userMessage: '模型回复达到输出长度上限，本次处理未完成。请缩小本次处理范围后继续。', actionLabel: '',
  },
  model_quota_exhausted: {
    title: '模型服务配额不可用', userMessage: '模型服务暂时无法提供所需配额。请联系管理员检查额度或限额。', actionLabel: '',
  },
  model_rate_limited: {
    title: '模型服务请求过多', userMessage: '模型服务正在限流，本次处理未完成。请稍后继续。', actionLabel: '',
  },
  model_auth_failed: {
    title: '模型服务鉴权失败', userMessage: '模型服务鉴权未通过。请联系管理员检查凭证与服务权限。', actionLabel: '',
  },
  model_timeout: {
    title: '模型响应超时', userMessage: '模型服务未在规定时间内完成响应。请稍后继续。', actionLabel: '',
  },
  model_network_error: {
    title: '模型服务连接失败', userMessage: '无法连接模型服务，本次处理未完成。请稍后继续。', actionLabel: '',
  },
  model_stream_interrupted: {
    title: '模型响应中断', userMessage: '模型响应在完成前中断，本次处理未完成。请稍后继续。', actionLabel: '',
  },
  model_content_filtered: {
    title: '内容未通过模型服务检查', userMessage: '本次内容未通过模型服务检查。请调整请求后继续。', actionLabel: '',
  },
  model_invalid_response: {
    title: '模型返回内容无效', userMessage: '模型返回内容未能通过结果校验，本次处理未完成。请稍后继续。', actionLabel: '',
  },
  model_provider_unavailable: {
    title: '模型服务暂不可用', userMessage: '模型服务暂时无法处理请求。请稍后继续。', actionLabel: '',
  },
  model_provider_rejected: {
    title: '模型服务拒绝本次请求', userMessage: '模型服务拒绝了本次请求。请联系管理员核对模型配置和请求限制。', actionLabel: '',
  },
  model_provider_error: {
    title: '模型处理失败', userMessage: '模型服务未能完成本次处理，已保留当前任务进度。请稍后继续。', actionLabel: '',
  },
  message_sync_failed: {
    title: '消息同步失败', userMessage: '暂时无法同步消息，请重试同步。', actionLabel: '重试同步',
  },
  authentication_required: {
    title: '需要重新登录', userMessage: '登录状态已失效，请重新登录后继续。', actionLabel: '',
  },
  access_denied: {
    title: '当前操作不可用', userMessage: '你没有执行此操作的权限。', actionLabel: '',
  },
  session_state_changed: {
    title: '任务状态已变化', userMessage: '请刷新当前任务后继续。', actionLabel: '',
  },
  service_temporarily_unavailable: {
    title: '服务暂时不可用', userMessage: '服务尚未就绪，请稍后重试。', actionLabel: '重试',
  },
  workspace_configuration_unavailable: {
    title: '当前工作空间不可用', userMessage: '当前工作空间尚未配置，请联系管理员。', actionLabel: '',
  },
  workflow_execution_failed: {
    title: '任务执行失败', userMessage: '已保留当前任务进度，请稍后继续处理。', actionLabel: '',
  },
  run_budget_exhausted: {
    title: '本次处理未完成', userMessage: '本次处理未能完成，已达到尝试上限。', actionLabel: '',
  },
  invalid_request: {
    title: '当前请求无法处理', userMessage: '请检查输入后再试。', actionLabel: '',
  },
});

const recoveryActionLabel = (action, label = '') => (
  { retry_request: label || '重试' }[action] ?? ''
);

export function resolveAssistantErrorDisplay(error) {
  const record = Object.assign({}, error);
  const recovery = Object.assign({}, record.recovery);
  const rawMessage = String(record.message ?? '').trim();
  const displayKey = String(recovery.displayKey ?? '').trim();
  const recoveryDisplay = ERROR_CODE_DISPLAYS[String(record.code ?? '')] ?? RECOVERY_DISPLAYS[displayKey];
  if (recoveryDisplay) {
    const actionLabel = recoveryActionLabel(recovery.action, recoveryDisplay.actionLabel);
    return { ...recoveryDisplay, technicalMessage: rawMessage, actionLabel };
  }
  const normalizedType = String(record.type ?? '').trim().toLowerCase();
  const lower = rawMessage.toLowerCase();
  const isNetwork = [
    normalizedType === 'network',
    lower.includes('failed to fetch'),
    lower.includes('network'),
  ].some(Boolean);
  const fallbackMessage = { none: '本次处理未能完成。' }[recovery.action] ?? ASSISTANT_REGENERATE_MESSAGE;
  const userMessage = isNetwork ? NETWORK_ERROR_MESSAGE : fallbackMessage;
  return {
    title: ASSISTANT_ERROR_TITLE,
    userMessage,
    technicalMessage: rawMessage,
    actionLabel: recoveryActionLabel(recovery.action),
  };
}

export function resolveAssistantMessageErrorDisplay({ role = '', content = '' } = {}) {
  if (String(role || '').trim() !== 'assistant') {
    return null;
  }
  const rawContent = String(content || '').trim();
  if ([Boolean(rawContent), PROVIDER_PLACEHOLDER_PATTERN.test(rawContent)].some((value) => !value)) {
    return null;
  }
  return {
    title: ASSISTANT_ERROR_TITLE,
    userMessage: ASSISTANT_REGENERATE_MESSAGE,
    technicalMessage: rawContent,
  };
}
